import axios from "axios";

// Use relative URL /api for same-origin requests (works in both preview and production)
// Fall back to REACT_APP_BACKEND_URL only if current origin doesn't match
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const isSameOrigin = typeof window !== 'undefined' && BACKEND_URL.startsWith(window.location.origin);
const API = isSameOrigin || !BACKEND_URL ? '/api' : `${BACKEND_URL}/api`;

const axiosInstance = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('auth_token');
      // Redirect to login if not already on auth-related pages
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/signup' && path !== '/forgot-password' && !path.startsWith('/auth')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const api = {
  // ==================== AUTH ====================
  async signup(email, password, name) {
    const response = await axiosInstance.post("/auth/signup", { email, password, name });
    return response.data;
  },

  async login(email, password) {
    const response = await axiosInstance.post("/auth/login", { email, password });
    return response.data;
  },

  async googleAuth(sessionId) {
    const response = await axiosInstance.post("/auth/google", { session_id: sessionId });
    return response.data;
  },

  async getMe() {
    const response = await axiosInstance.get("/auth/me");
    return response.data;
  },

  async logout() {
    const response = await axiosInstance.post("/auth/logout");
    return response.data;
  },

  async changePassword(currentPassword, newPassword) {
    const response = await axiosInstance.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  async requestPasswordReset(email) {
    const response = await axiosInstance.post("/auth/password-reset-request", { email });
    return response.data;
  },

  async confirmPasswordReset(token, newPassword) {
    const response = await axiosInstance.post("/auth/password-reset-confirm", { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  },

  // ==================== ADMIN ====================
  async getUsers() {
    const response = await axiosInstance.get("/admin/users");
    return response.data;
  },

  async updateUser(userId, data) {
    const response = await axiosInstance.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  async getAllPortfolios() {
    const response = await axiosInstance.get("/admin/portfolios");
    return response.data;
  },

  // ==================== ASSET LOOKUP (PUBLIC - for all users) ====================
  async getAssetLookupList(assetClass = null, geography = null) {
    const params = {};
    if (assetClass) params.asset_class = assetClass;
    if (geography) params.geography = geography;
    const response = await axiosInstance.get("/asset-lookup/list", { params });
    return response.data;
  },

  async getAssetLookupAssetClasses() {
    const response = await axiosInstance.get("/asset-lookup/asset-classes");
    return response.data;
  },

  async getAssetLookupGeographies() {
    const response = await axiosInstance.get("/asset-lookup/geographies");
    return response.data;
  },

  // ==================== ASSET LOOKUP (ADMIN) ====================
  async getAssetLookups(search = null, assetClass = null) {
    const params = {};
    if (search) params.search = search;
    if (assetClass) params.category = assetClass;
    const response = await axiosInstance.get("/admin/asset-lookup", { params });
    return response.data;
  },

  async getAssetClasses() {
    const response = await axiosInstance.get("/admin/asset-lookup/asset-classes");
    return response.data;
  },

  async createAssetLookup(data) {
    const response = await axiosInstance.post("/admin/asset-lookup", data);
    return response.data;
  },

  async updateAssetLookup(assetId, data) {
    const response = await axiosInstance.put(`/admin/asset-lookup/${assetId}`, data);
    return response.data;
  },

  async deleteAssetLookup(assetId) {
    const response = await axiosInstance.delete(`/admin/asset-lookup/${assetId}`);
    return response.data;
  },

  async importAssetFromYahoo(symbol, assetClassEn, assetClassDe, assetClassFr = "", sectorEn = "", sectorDe = "", sectorFr = "", factorEn = "", factorDe = "", factorFr = "", geographyEn = "", geographyDe = "", geographyFr = "") {
    const response = await axiosInstance.post("/admin/asset-lookup/import-from-yahoo", null, {
      params: { 
        symbol, 
        asset_class_en: assetClassEn, 
        asset_class_de: assetClassDe,
        asset_class_fr: assetClassFr,
        sector_en: sectorEn,
        sector_de: sectorDe,
        sector_fr: sectorFr,
        factor_en: factorEn,
        factor_de: factorDe,
        factor_fr: factorFr,
        geography_en: geographyEn,
        geography_de: geographyDe,
        geography_fr: geographyFr
      }
    });
    return response.data;
  },

  async clearPriceCache() {
    const response = await axiosInstance.delete("/admin/price-cache");
    return response.data;
  },

  // ==================== PORTFOLIOS ====================
  async getPortfolios() {
    const response = await axiosInstance.get("/portfolios");
    return response.data;
  },

  async getPortfolio(id) {
    const response = await axiosInstance.get(`/portfolios/${id}`);
    return response.data;
  },

  async createPortfolio(data) {
    const response = await axiosInstance.post("/portfolios", data);
    return response.data;
  },

  async updatePortfolio(id, data) {
    const response = await axiosInstance.put(`/portfolios/${id}`, data);
    return response.data;
  },

  async deletePortfolio(id) {
    const response = await axiosInstance.delete(`/portfolios/${id}`);
    return response.data;
  },

  // ==================== ASSETS ====================
  async addAsset(portfolioId, asset) {
    const response = await axiosInstance.post(`/portfolios/${portfolioId}/assets`, asset);
    return response.data;
  },

  async removeAsset(portfolioId, symbol) {
    const response = await axiosInstance.delete(`/portfolios/${portfolioId}/assets/${symbol}`);
    return response.data;
  },

  async updateAssetQuantity(portfolioId, symbol, quantity) {
    const response = await axiosInstance.put(`/portfolios/${portfolioId}/assets/${symbol}`, { quantity });
    return response.data;
  },

  // ==================== SEARCH & PRICES ====================
  async searchSymbols(query, nameSearch = null) {
    const params = {};
    if (query) params.q = query;
    if (nameSearch) params.name = nameSearch;
    const response = await axiosInstance.get("/search", { params });
    return response.data;
  },

  async getPrices(symbol, period = "1Y") {
    const response = await axiosInstance.get(`/prices/${symbol}`, { params: { period } });
    return response.data;
  },

  async getQuote(symbol) {
    const response = await axiosInstance.get(`/quote/${symbol}`);
    return response.data;
  },

  async getKPIs(symbol) {
    const response = await axiosInstance.get(`/kpis/${symbol}`);
    return response.data;
  },

  async getExchangeRates(date = null) {
    const params = date ? { date } : {};
    const response = await axiosInstance.get("/exchange-rates", { params });
    return response.data;
  },

  // ==================== PERFORMANCE ====================
  async getPortfolioPerformance(portfolioId, period = "1Y", startDate = null) {
    const params = { period, skip_first: 1 };
    if (startDate) {
      params.start_date = startDate;
    }
    const response = await axiosInstance.get(`/portfolios/${portfolioId}/performance`, { params });
    return response.data;
  },

  // ==================== AI ANALYSIS ====================
  async analyzePortfolio(portfolioId, language = "en") {
    const response = await axiosInstance.post(`/portfolios/${portfolioId}/analyze`, { language });
    return response.data;
  },

  async analyzeAsset(symbol, language = "en") {
    const response = await axiosInstance.post(`/assets/${symbol}/analyze`, { language });
    return response.data;
  },

  // ==================== RISK PROFILE ====================
  async getRiskProfile() {
    const response = await axiosInstance.get('/risk-profile');
    return response.data;
  },

  async saveRiskAttitude(answers) {
    const response = await axiosInstance.post('/risk-profile/attitude', answers);
    return response.data;
  },

  async saveRiskCapacity(answers) {
    const response = await axiosInstance.post('/risk-profile/capacity', answers);
    return response.data;
  },
};

export default api;
